import React, { useState, useEffect } from 'react';
import {
  useForm,
  Controller,
  SubmitHandler,
  FormProvider,
  SubmitErrorHandler,
} from 'react-hook-form';
import {
  Button,
  TextField,
  SelectionControlLabel,
  Checkbox,
  LoadingButton,
  DropdownField,
  MenuItem,
  Typography,
} from '@one-thd/sui-atomic-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { setOverlayEvent, track } from '../../../utils/analytics';
import { INVALID_PAYMENT_ERROR_CODES } from '../../../constants/error-codes';
import { Alert } from '../../shared/alert/alert';
import {
  ERR_GENERIC,
  ERR_INVALID_PAYMENT,
  ERR_UNABLE_TO_SAVE_PAYMENT,
} from '../../../constants/response-error-messages';
import { useIsMounted } from '../../../hooks/use-is-mounted';
import { CHANGES_SAVED_MSG } from '../../../constants/response-success-messages';
import { DefaultCreditCardModal } from '../../shared/default-credit-card-modal/default-credit-card-modal';
import { b2cPaymentsApi } from '../../../api/b2c/payments';
import {
  IB2CPaymentCard,
  TB2CEditPaymentRequestData,
} from '../../../models/b2c/payments';
import {
  mapB2BEditCreditCardToRequestModel,
  mapB2CEditCreditCardToRequestModel,
} from '../../../utils/mapping-payments';
import {
  IB2BBillingAddress,
  IB2BPaymentCard,
} from '../../../models/b2b/payments';
import { Spinner } from '../../shared/spinner/spinner';
import { b2bPaymentsApi } from '../../../api/b2b/payments';
import { CreditCardBrands } from '../../../constants/credit-card';
import { ZipCodeCitySelectFields } from '../../shared/zipcode/zipcode';
import {
  getCreditCardFormValues,
  getTextFieldStatus,
  isAllowedCharacter,
} from '../../../utils/form';
import {
  IPaymentCardCommonData,
  TCommonPartialPaymentCards,
  TPaymentErrorResponse,
  TPaymentFormValues,
} from '../../../models/common/payments';
import { usePayments } from '../../../hooks/use-payments';
import { FormTitle } from '../../shared/form-title/form-title';
import {
  expirationMonthValues,
  expirationYearValues,
  getPaymentsErrorCode,
} from '../../../utils/payments';
import {
  TAlertStatus,
  TErrorMessage,
} from '../../../models/common/alert-messages';
import { useModal } from '../../../hooks/use-modal';
import { useAddresses } from '../../../hooks/use-addresses';
import { useDrawer } from '../../../hooks/use-drawer';
import { CardType } from '../../../constants/payments';
import { b2bValidationSchema, b2cValidationSchema } from './validation';
import {
  setEditCardAnalytics,
  setSuccessSetAsDefaultCardAnalytics,
} from '../../../utils/payments-analytics';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { TErrorTracking } from '../../../models/common/analytics';
import { TextButton } from '../../shared/text-button/text-button';
import { AddressDropdownSelector } from '../../shared/address-select/address-dropdown';
import { SelectBillingAddress } from '../../shared/select-billing-address/select-billing-address';
import { AddBillingAddress } from '../../shared/add-billing-address/add-billing-address';
import { ERROR_CODES } from '../../../constants/custom-errors';
import { CustomerRole } from '../../../models/common/customer';

interface IEditCreditCardForm {
  card: IPaymentCardCommonData;
  onCancelForm: () => void;
  onEditSuccess: () => void;
  fromRemotePurchases: boolean;
}

export const EditCreditCardForm = ({
  card,
  onCancelForm,
  onEditSuccess,
  fromRemotePurchases,
}: IEditCreditCardForm) => {
  const [loading, setLoading] = useState(false);
  const [initialAddressLoading, setInitialAddressLoading] = useState(false);
  const [billingAddress, setBillingAddress] =
    useState<IB2BBillingAddress | null>(null);
  const [errorMsg, setErrorMsg] = useState<TErrorMessage | null>(null);
  const { isB2B, creditCards, setAlertMessage } = usePayments();
  const { openModal, hideModal, setComponentProps } = useModal();
  const { getAddresses } = useAddresses();
  const { openDrawer, hideDrawer } = useDrawer();
  const isMountedRef = useIsMounted();
  const { role } = useCustomerInfo();

  const isPurchaser = role === CustomerRole.PURCHASER;
  const addressId = card?.addressId;
  const defaultValues = getCreditCardFormValues(card);
  const citiAccountId = card?.citiAccountId;
  const paymentId = card?.paymentId;
  const isPropCardType = card?.cardBrand === CreditCardBrands.PROP;
  const validationSchema = isB2B ? b2bValidationSchema : b2cValidationSchema;
  const formMethods = useForm<TPaymentFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues,
    mode: 'onTouched',
    context: { isB2B },
  });

  const {
    control,
    formState: { errors: formErrors, isValid, dirtyFields },
    setValue,
    getValues,
    handleSubmit,
    trigger: triggerFormValidation,
  } = formMethods;

  useEffect(() => {
    if (addressId && isB2B) {
      setLoading(true);
      setInitialAddressLoading(true);
      b2bPaymentsApi
        .getBillingAddress(`${addressId}`, isPurchaser)
        .then(({ data }) => {
          const { billingAddress: address } = data;
          setBillingAddressValues(address);
          setBillingAddress(address);
          triggerFormValidation();
        })
        .catch((error) => {
          const errorCode =
            error?.response?.data?.addresses?.errors[0]?.errorCode;
          setErrorMsg(ERR_GENERIC);

          track<TErrorTracking>('credit-card.actions.post-page-error', {
            type: 'error',
            displayType: 'inline',
            pageSection: 'edit credit card',
            message: ERR_GENERIC.message,
            code: errorCode,
          });
        })
        .finally(() => {
          setLoading(false);
          setInitialAddressLoading(false);
        });
    }
    if (!isB2B) {
      getAddresses({ page: 0, pageSize: 0 }).catch((error) => {
        if (error.message === ERROR_CODES.NO_SAVED_ADR_CODE) {
          return;
        }
        throw error;
      });
    }
    if (!addressId) triggerFormValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isZipCodePending, isZipCodeApiError, isHDCard } = getValues();

  const isFormDirty = () =>
    Object.values(dirtyFields).some((value) => {
      return value || false;
    });

  const handleOnKeyPress = (
    event: KeyboardEvent,
    field: keyof TPaymentFormValues,
  ) => !isAllowedCharacter(event.key, field) && event.preventDefault();

  const setBillingAddressValues = (address: IB2BBillingAddress) => {
    const cityState = `${address.cityName}, ${address.stateCode}`;
    setValue('addressLine1', address.addressLine1 || '');
    setValue('addressLine2', address.addressLine2 || '');
    setValue('cityState', cityState);
    setValue('zipCode', address.postalCode || '');
  };

  const setSuccessMsg = () => {
    if (card) {
      setAlertMessage({
        paymentId: card.paymentId,
        alertStatus: TAlertStatus.SUCCESS,
        text: CHANGES_SAVED_MSG.message,
        dataTestId: CHANGES_SAVED_MSG.dataTestId,
      });
    }
  };
  const onSuccessHandler = (isDefault: boolean) => {
    onEditSuccess();
    setSuccessMsg();
    setEditCardAnalytics(
      'credit card',
      isDefault,
      card.cardBrand,
      card.cardType,
    );
    if (!defaultValues.isDefault && isDefault) {
      setSuccessSetAsDefaultCardAnalytics();
    }
  };

  const onErrorHandler = (err: AxiosError<TPaymentErrorResponse>) => {
    const errorCode = getPaymentsErrorCode(err) || '';
    if (INVALID_PAYMENT_ERROR_CODES[errorCode]) {
      setErrorMsg(ERR_INVALID_PAYMENT);

      track<TErrorTracking>('credit-card.actions.post-page-error', {
        type: 'error',
        displayType: 'inline',
        pageSection: 'edit credit card',
        message: ERR_INVALID_PAYMENT.message,
        code: errorCode,
      });

      return;
    }
    setErrorMsg(ERR_UNABLE_TO_SAVE_PAYMENT);

    track<TErrorTracking>('credit-card.actions.post-page-error', {
      type: 'error',
      displayType: 'inline',
      pageSection: 'edit credit card',
      message: ERR_UNABLE_TO_SAVE_PAYMENT.message,
      code: errorCode || 'n/a',
    });
  };

  const executeUpdateCreditCard = (
    creditCardRequest: IB2BPaymentCard | TB2CEditPaymentRequestData,
  ) => {
    const { isDefault } = creditCardRequest;
    if (isB2B) {
      return b2bPaymentsApi
        .updatePayment(
          creditCardRequest as IB2BPaymentCard,
          CardType.CREDIT_CARD,
        )
        .then(() => {
          onSuccessHandler(isDefault);
        })
        .catch(onErrorHandler);
    }
    return b2cPaymentsApi
      .updateCreditCard(creditCardRequest as IB2CPaymentCard)
      .then(() => {
        onSuccessHandler(isDefault);
      })
      .catch(onErrorHandler);
  };

  const onSubmitForm: SubmitHandler<TPaymentFormValues> = async (values) => {
    if (!isFormDirty()) {
      onCancelForm();
      return;
    }

    const isFormValid = !isZipCodePending || !isZipCodeApiError || isValid;

    if (isFormValid) {
      const creditCardDataFromResponse = (
        creditCards?.response as TCommonPartialPaymentCards[]
      ).find((newCard) => newCard.paymentId === paymentId);

      if (creditCardDataFromResponse) {
        const creditCardRequest: IB2BPaymentCard | TB2CEditPaymentRequestData =
          isB2B
            ? mapB2BEditCreditCardToRequestModel(
                values,
                billingAddress,
                creditCardDataFromResponse as IB2BPaymentCard,
              )
            : mapB2CEditCreditCardToRequestModel(
                values,
                creditCardDataFromResponse as IB2CPaymentCard,
              );

        if (values.isDefault && !defaultValues.isDefault) {
          openModal(DefaultCreditCardModal, {
            card,
            onConfirmDefault: () => {
              setComponentProps({ isLoading: true });
              executeUpdateCreditCard(creditCardRequest).finally(() => {
                hideModal();
              });
            },
            isLoading: false,
          });
          setOverlayEvent('confirm your new default payment method');
        } else {
          setLoading(true);
          executeUpdateCreditCard(creditCardRequest).finally(() => {
            if (isMountedRef.current) setLoading(false);
          });
        }
      }
    }
  };

  const onInvalid: SubmitErrorHandler<TPaymentFormValues> | undefined = async (
    values,
  ) => {
    // Convert messages to a Set to ensure uniqueness
    const uniqueMessages = new Set(
      Object.values(values).map((entry) => entry.message),
    );
    const combinedMessages = Array.from(uniqueMessages).join(' ');

    track<TErrorTracking>('credit-card.actions.post-page-error', {
      type: 'advisory',
      displayType: 'inline',
      pageSection: 'edit credit card',
      message: combinedMessages,
    });
  };

  const onViewAll = () => {
    openDrawer(SelectBillingAddress, {
      hideDrawer,
      onSave: (newAddrId: number) => {
        setValue('billingAddressID', newAddrId, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
      getValues,
      onAddBilling,
    });
  };
  const onAddBilling = () => {
    openDrawer(AddBillingAddress, {
      hideDrawer,
      onAdd: (newAddrId: number) => {
        setValue('billingAddressID', newAddrId, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    });
  };

  return (
    <>
      <Spinner active={initialAddressLoading} />
      <div className="lg:sui-max-w-[437px] sui-flex sui-flex-col sui-gap-6">
        <FormTitle
          title="EDIT CREDIT CARD"
          actionLabel="Cancel"
          onActionClick={onCancelForm}
        />
        {fromRemotePurchases && (
          <Typography variant="body-base">
            Additional card details are required to authorize it for remote
            purchases.
          </Typography>
        )}
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmitForm, onInvalid)}>
            <div className="sui-grid sui-gap-y-6 sui-mb-10">
              <Controller
                name="cardNickName"
                control={control}
                render={({ field }) => {
                  const { ref, value, name, onChange, ...rest } = field;
                  return (
                    <TextField
                      label="Card Nickname"
                      status={getTextFieldStatus(formErrors.cardNickName)}
                      statusMessage={formErrors.cardNickName?.message}
                      optional
                      value={value || ''}
                      name={name}
                      onChange={onChange}
                      InputProps={{
                        inputProps: {
                          ...rest,
                          'data-testid': 'nicknameField',
                          maxLength: 100,
                          'data-qm-mask': '',
                        },
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="cardNumber"
                control={control}
                render={({ field }) => {
                  const { ref, value, name, onChange, ...rest } = field;
                  return (
                    <TextField
                      label="Card Number*"
                      disabled
                      statusMessage={formErrors.cardNumber?.message}
                      value={value}
                      name={name}
                      onChange={onChange}
                      InputProps={{
                        inputProps: { ...rest, 'data-qm-mask': '' },
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="cardHolderName"
                control={control}
                render={({ field }) => {
                  const { ref, value, name, onChange, ...rest } = field;
                  return (
                    <TextField
                      disabled={!!citiAccountId}
                      label={isPropCardType ? 'Name on Card' : 'Name on Card*'}
                      status={getTextFieldStatus(formErrors.cardHolderName)}
                      statusMessage={formErrors.cardHolderName?.message}
                      value={value}
                      name={name}
                      onChange={onChange}
                      InputProps={{
                        inputProps: {
                          ...rest,
                          'data-qm-mask': '',
                        },
                      }}
                      optional={isPropCardType}
                    />
                  );
                }}
              />
              {!isHDCard && (
                <div className="sui-grid sui-grid-cols-2 sui-gap-x-6">
                  <div>
                    <Controller
                      name="cardExpiryMonth"
                      control={control}
                      render={({ field }) => {
                        const {
                          ref,
                          onChange,
                          value: fieldValue,
                          ...rest
                        } = field;
                        return (
                          <DropdownField
                            fullWidth
                            label="Expiration Month*"
                            value={fieldValue}
                            id="cardExpiryMonth"
                            status={getTextFieldStatus(
                              formErrors.cardExpiryMonth,
                            )}
                            statusMessage={formErrors.cardExpiryMonth?.message}
                            dropdownProps={{
                              onChange: async (
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                onChange(event);
                                await formMethods.trigger(['cardExpiryYear']);
                              },
                              ...rest,
                            }}
                          >
                            {expirationMonthValues.map(
                              ({ value: itemValue, label }) => (
                                <MenuItem key={itemValue} value={itemValue}>
                                  {label}
                                </MenuItem>
                              ),
                            )}
                          </DropdownField>
                        );
                      }}
                    />
                  </div>
                  <div>
                    <Controller
                      name="cardExpiryYear"
                      control={control}
                      render={({ field }) => {
                        const {
                          ref,
                          onChange,
                          value: fieldValue,
                          ...rest
                        } = field;
                        return (
                          <DropdownField
                            fullWidth
                            label="Expiration Year*"
                            value={fieldValue}
                            id="cardExpiryYear"
                            status={getTextFieldStatus(
                              formErrors.cardExpiryYear,
                            )}
                            statusMessage={formErrors.cardExpiryYear?.message}
                            dropdownProps={{
                              onChange: async (
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                onChange(event);
                                await formMethods.trigger(['cardExpiryMonth']);
                              },
                              ...rest,
                            }}
                          >
                            {expirationYearValues.map(({ value, label }) => (
                              <MenuItem key={value} value={value}>
                                {label}
                              </MenuItem>
                            ))}
                          </DropdownField>
                        );
                      }}
                    />
                  </div>
                </div>
              )}
              {isB2B ? (
                <>
                  <Controller
                    name="addressLine1"
                    control={control}
                    render={({ field }) => {
                      const { ref, value, name, onChange, ...rest } = field;
                      return (
                        <TextField
                          label="Billing Address*"
                          status={getTextFieldStatus(formErrors.addressLine1)}
                          statusMessage={formErrors.addressLine1?.message}
                          value={value}
                          name={name}
                          onChange={onChange}
                          InputProps={{
                            inputProps: {
                              ...rest,
                              onKeyPress: (event: KeyboardEvent) => {
                                handleOnKeyPress(event, 'addressLine1');
                              },
                              maxLength: 60,
                              'data-qm-mask': '',
                            },
                          }}
                        />
                      );
                    }}
                  />
                  <Controller
                    name="addressLine2"
                    control={control}
                    render={({ field }) => {
                      const { ref, value, name, onChange, ...rest } = field;
                      return (
                        <TextField
                          label="Billing Address Line 2"
                          status={getTextFieldStatus(formErrors.addressLine2)}
                          statusMessage={formErrors.addressLine2?.message}
                          optional
                          value={value || ''}
                          name={name}
                          onChange={onChange}
                          InputProps={{
                            inputProps: {
                              ...rest,
                              onKeyPress: (event: KeyboardEvent) => {
                                handleOnKeyPress(event, 'addressLine2');
                              },
                              maxLength: 60,
                              'data-qm-mask': '',
                            },
                          }}
                        />
                      );
                    }}
                  />
                  {!initialAddressLoading && <ZipCodeCitySelectFields />}
                </>
              ) : (
                <>
                  <Controller
                    name="billingAddressID"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => {
                      const {
                        value: fieldValue,
                        onChange,
                        ref,
                        ...rest
                      } = field;
                      return (
                        <AddressDropdownSelector
                          status={getTextFieldStatus(
                            formErrors.billingAddressID,
                          )}
                          statusMessage={formErrors.billingAddressID?.message}
                          selectedAddrID={fieldValue}
                          onChange={(addrId: number) => {
                            onChange(addrId);
                          }}
                          {...rest}
                        />
                      );
                    }}
                  />
                  <div className="sui-grid sui-grid-cols-2 sui-gap-32 sm:sui-gap-x-32">
                    {onViewAll && (
                      <TextButton className="sui-text-left" onClick={onViewAll}>
                        View all addresses
                      </TextButton>
                    )}
                    {onAddBilling && (
                      <TextButton
                        className="sui-text-right"
                        onClick={onAddBilling}
                        dataTestId="add-address-btn"
                      >
                        + Add Address
                      </TextButton>
                    )}
                  </div>
                </>
              )}
              <div className="sui-my-2">
                <SelectionControlLabel label="Make this my default payment method">
                  <Controller
                    name="isDefault"
                    control={control}
                    render={({ field: { ref, value, ...rest } }) => (
                      <Checkbox
                        {...rest}
                        checked={value}
                        disabled={
                          !!card?.isDefault ||
                          (isB2B && !card?.actions?.setasDefault)
                        }
                      />
                    )}
                  />
                </SelectionControlLabel>
              </div>
              {errorMsg && <Alert status="error">{errorMsg.message}</Alert>}
              <div className="sui-grid sui-grid-cols-1 sui-gap-5 sm:sui-grid-cols-2">
                <div className="sui-order-last sm:sui-order-first">
                  <Button fullWidth variant="secondary" onClick={onCancelForm}>
                    Cancel
                  </Button>
                </div>
                <LoadingButton
                  loading={loading && !initialAddressLoading}
                  fullWidth
                  variant="primary"
                  type="submit"
                  data-testid="submitBtn"
                >
                  Save Changes
                </LoadingButton>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
};
